import { TypedDocumentNode, gql } from '@apollo/client';

import { ManagerProgressionTaskState } from '__generated__/globalTypes';
import { useCurrentUserContext } from 'contexts/currentUser';
import { FootballTasksUpdate, useLifecycle } from 'hooks/useLifecycle';
import { withFragments } from 'lib/gql';
import { FragmentOf, unmaskFragment } from 'lib/graphql';

import type { useCountHighlightedMissions_currentUser } from './__generated__/useCountHighlightedMissions.graphql';

const countCompletedTasks = (
  tasks: Nullable<{ aasmState: ManagerProgressionTaskState }[]>
) => {
  return (
    tasks?.filter(
      task => task?.aasmState === ManagerProgressionTaskState.COMPLETED
    ).length || 0
  );
};

const includeHighlightedCount = (
  completedTaskCount: number,
  taskUpdateCount: FootballTasksUpdate | undefined
) => {
  if (completedTaskCount > 0) {
    return completedTaskCount;
  }
  if (taskUpdateCount && taskUpdateCount !== 'seen') {
    return 1;
  }
  return 0;
};

export const useCountHighlightedMissions = withFragments(
  ({
    currentUser: currentUser_,
  }: {
    currentUser: Nullable<FragmentOf<useCountHighlightedMissions_currentUser>>;
  }) => {
    const { lifecycle } = useLifecycle();
    const { currentUser: contextCurrentUser } = useCurrentUserContext();

    const currentUser = unmaskFragment(currentUser_);

    const isOnboarding = (currentUser?.onboardingTasks?.length || 0) > 0;

    if (contextCurrentUser?.forcedIntoOnboarding) {
      return 0;
    }

    if (isOnboarding) {
      return includeHighlightedCount(
        countCompletedTasks(currentUser?.onboardingTasks),
        lifecycle?.footballDailyTasksUpdate
      );
    }
    return (
      includeHighlightedCount(
        countCompletedTasks(currentUser?.daily_tasks),
        lifecycle?.footballDailyTasksUpdate
      ) +
      includeHighlightedCount(
        countCompletedTasks(currentUser?.weekly_tasks),
        lifecycle?.footballGameWeeklyTasksUpdate
      ) +
      includeHighlightedCount(
        countCompletedTasks(currentUser?.game_weekly_tasks),
        lifecycle?.footballHotStreakTasksUpdate
      )
    );
  },
  {
    currentUser: gql`
      fragment useCountHighlightedMissions_currentUser on CurrentUser {
        slug
        onboardingTasks(sport: $sport) {
          id
          aasmState
        }
        daily_tasks: tasks(sport: $sport, periodicity: DAILY) {
          id
          aasmState
        }
        weekly_tasks: tasks(sport: $sport, periodicity: WEEKLY) {
          id
          aasmState
        }
        game_weekly_tasks: tasks(sport: $sport, periodicity: GAME_WEEKLY) {
          id
          aasmState
        }
      }
    ` as TypedDocumentNode<useCountHighlightedMissions_currentUser>,
  }
);
